import { useMemo } from 'react';

import { fetcherToPlainText } from '../../../../shared/fetchers';
import useOrganizationSettings from '../shared/useOrganizationSettings';
import type { FetcherConfigByLanguage, FetcherWithAuthSupported } from '../../../../typings/auth';
import type { OrganizationSettingsEndpoint, SettingsSection } from '../shared/useOrganizationSettings';

//  Return the OrganizationSettingsEndpoint for the given language.
const getEndpoint = (
  orgId: number,
  language: FetcherWithAuthSupported,
): OrganizationSettingsEndpoint =>
  `/api/organizations/${orgId}/settings/languages/${language}`;

/**
 * Hook for fetching the default general project settings.
 */
function useLanguageSettings<T extends FetcherWithAuthSupported>(
  orgId: number,
  language: T,
) {
  const {
    settings,
    isLoading,
    errors,
    onUpdateSettings,
    onSaveSettings,
    onSaveSettingsWithOverride,
    isSaveButtonDisabled,
  } = useOrganizationSettings<FetcherConfigByLanguage<T>>({
    endpoint: useMemo(() => getEndpoint(orgId, language), [orgId, language]),
    settingsSection: fetcherToPlainText(language) as SettingsSection,
  });

  return {
    settings,
    isLoading: isLoading.fetch,
    isSaving: isLoading.save,
    errors,
    onUpdateSettings,
    onSaveSettings,
    onSaveSettingsWithOverride,
    isSaveButtonDisabled,
  };
}

export default useLanguageSettings;
